import { useState } from 'react';
import {
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useMyProjects, useBulkAssignProjectResources } from '@/api/projects';
import type { CDNDomain } from '@/api/manticore';

/**
 * Assign the selected CDN domains to a project via the generic
 * project_resources mapping (bulk). Once assigned, the domains can be filtered
 * by that project (and any team that owns it) on the domain list.
 */
export function DomainAssignDialog({
  open,
  onOpenChange,
  domains,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  domains: CDNDomain[];
  onDone: () => void;
}) {
  const { show } = useToast();
  const { data: projects = [] } = useMyProjects();
  const [projectId, setProjectId] = useState('');
  const count = domains.length;

  const assign = useBulkAssignProjectResources({
    onSuccess: () => {
      show({ tone: 'success', title: `Assigned ${count} domain${count === 1 ? '' : 's'}` });
      onDone();
      onOpenChange(false);
      setProjectId('');
    },
    onError: () => show({ tone: 'danger', title: 'Assign failed' }),
  });

  const submit = () => {
    if (!projectId) return;
    assign.mutate({
      projectId,
      action: 'assign',
      resources: domains.map((d) => ({
        service_id: 'cdn',
        resource_type: 'domain',
        resource_id: String(d.id),
        resource_name: d.domain,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign {count} domain{count === 1 ? '' : 's'} to a project</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              The selected domains will appear under the chosen project and any team that owns it.
            </p>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
              <SelectContent>
                {projects.length === 0 ? (
                  <SelectItem value="__none__" disabled>No projects available</SelectItem>
                ) : (
                  projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!projectId || assign.isPending} onClick={submit}>Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
